/**
 * CitationChip — inline source reference in the answer prose.
 *
 * Collapsed: compact pill showing source index + name.
 * Expanded: full ArtifactCard with collapse / close controls.
 */
import { useState } from "react";
import { FileText } from "lucide-react";
import { ArtifactCard } from "@/components/molecules/multi-agent/ArtifactCard";
import { sanitizeArtifactLabel } from "@/services/multi-agent/artifactDisplay";
import type { MultiAgentState } from "@/stores/multiAgentStore";

type ArtifactRow = MultiAgentState["artifacts"][number];

interface CitationChipProps {
  index: number;
  artifact: ArtifactRow;
  /** @deprecated Prefer user click-to-expand; kept for explicit ?expand=true only. */
  expand?: boolean;
}

export function CitationChip({ index, artifact, expand = false }: CitationChipProps) {
  const [expanded, setExpanded] = useState(expand);
  const displayName = sanitizeArtifactLabel(artifact.name, artifact.id.slice(0, 8));

  if (expanded) {
    return (
      <div className="ma-artifact-block my-3 w-full">
        <ArtifactCard
          artifact={artifact}
          defaultOpen
          onCollapse={() => setExpanded(false)}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className="ma-citation-chip ma-interactive inline-flex max-w-[min(100%,280px)] cursor-pointer items-center gap-1.5 rounded-full border border-ma-accent-faint bg-ma-accent-soft px-2.5 py-0.5 align-middle text-[11px] font-medium leading-[1.3] text-ma-accent my-0.5 ml-1 mr-0"
      title={`View source ${index}: ${displayName}`}
      aria-label={`View source ${index}: ${displayName}`}
    >
      <FileText className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
      <span className="tabular-nums font-semibold">[{index}]</span>
      <span className="max-w-[160px] truncate font-normal">{displayName}</span>
    </button>
  );
}
